import * as React from 'react';
import { useState, useMemo, useCallback } from 'react';
import {
  Loader2,
  Search,
  Plus,
  Trash2,
  Pencil,
  ShoppingBag,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  SortingState,
  ColumnDef,
  flexRender,
} from '@tanstack/react-table';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API, graphqlOperation } from 'aws-amplify';
import Fuse from 'fuse.js';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';
import { useGetProducts } from '../../../application/routes/products/ProductsScreen/fetchProducts';
import { Product } from '../../../application/routes/products/ProductsScreen/types';
import { deleteProduct } from '../../../graphql/mutations';
import { ProductFormDialog } from './ProductFormDialog';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
    isAdmin?: boolean;
  };
};

export function ProductsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedProductFamily, setSelectedProductFamily] = useState('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] =
    useState<Partial<Product> | null>(null);

  const isAdmin = (global as any)?.me?.isAdmin === true;
  const { data: products = [], isLoading, error } = useGetProducts();

  const productFamilies = useMemo(() => {
    const families = Array.from(
      new Set(products.map((p) => p?.productFamily || '').filter(Boolean))
    );
    return families.sort();
  }, [products]);

  const filteredProducts = useMemo(() => {
    let filtered = products as Partial<Product>[];

    if (selectedProductFamily && selectedProductFamily !== 'all') {
      filtered = filtered.filter(
        (p) => p.productFamily === selectedProductFamily
      );
    }

    if (searchValue) {
      const fuse = new Fuse(filtered, {
        keys: [
          'name',
          'productCode',
          'productFamily',
          'id',
          'units',
          'listPrice',
        ],
        threshold: 0.3,
        includeScore: true,
      });
      filtered = fuse.search(searchValue).map((r) => r.item);
    }

    return filtered;
  }, [products, searchValue, selectedProductFamily]);

  const deleteMutation = useMutation({
    mutationFn: async (productId: string) => {
      return API.graphql(
        graphqlOperation(deleteProduct, { input: { id: productId } })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setIsDeleteModalOpen(false);
      setSelectedProduct(null);
      toast.success('Product deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete product');
    },
  });

  const handleDelete = useCallback((product: Partial<Product>) => {
    setSelectedProduct(product);
    setIsDeleteModalOpen(true);
  }, []);

  const handleEdit = useCallback((product: Partial<Product>) => {
    setSelectedProduct(product);
    setIsFormOpen(true);
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedProduct(null);
    setIsFormOpen(true);
  }, []);

  const confirmDelete = () => {
    if (selectedProduct?.id) {
      deleteMutation.mutate(selectedProduct.id);
    }
  };

  const refreshProducts = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['products'] });
  }, [queryClient]);

  const columns = useMemo<ColumnDef<Partial<Product>>[]>(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        cell: (info) => (
          <div className="flex items-center gap-2">
            <div className="size-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <ShoppingBag className="size-4 text-muted-foreground" />
            </div>
            <span className="font-semibold text-foreground">
              {info.getValue() as string}
            </span>
          </div>
        ),
      },
      { header: 'Product Family', accessorKey: 'productFamily' },
      { header: 'Product Code', accessorKey: 'productCode' },
      { header: 'ID', accessorKey: 'id' },
      { header: 'Units', accessorKey: 'units' },
      {
        header: 'Price',
        accessorKey: 'listPrice',
        cell: (info) => (
          <span className="font-semibold text-primary">
            ${Number(info.getValue()).toLocaleString()}
          </span>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: (info) => {
          const status = info.getValue() as string;
          return (
            <Badge
              variant={status === 'active' ? 'default' : 'secondary'}
              className="text-xs capitalize"
            >
              {status || 'active'}
            </Badge>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: filteredProducts,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Products"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Products"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex items-center justify-center flex-1">
          <p className="text-destructive text-lg">
            Something went wrong fetching the data. Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Products"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
        actions={
          isAdmin
            ? [
                {
                  id: 'new-product',
                  label: 'New product',
                  icon: <Plus className="size-4" />,
                  variant: 'primary',
                  onClick: handleCreate,
                },
              ]
            : undefined
        }
      />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
          <div className="relative w-full sm:w-60">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select
            value={selectedProductFamily}
            onValueChange={setSelectedProductFamily}
          >
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {productFamilies.map((family) => (
                <SelectItem key={family} value={family}>
                  {family}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        {table.getRowModel().rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 border border-dashed border-border rounded-xl">
            <ShoppingBag className="size-10 text-muted-foreground mb-3" />
            <p className="text-lg font-medium text-muted-foreground">
              No available products.
            </p>
          </div>
        ) : (
          <>
            <div className="border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      {table.getHeaderGroups()[0].headers.map((header) => (
                        <th
                          key={header.id}
                          onClick={() =>
                            header.column.getCanSort() &&
                            header.column.toggleSorting()
                          }
                          className={cn(
                            'text-left font-medium text-muted-foreground px-4 py-3',
                            header.column.getCanSort() &&
                              'cursor-pointer select-none hover:text-foreground'
                          )}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </th>
                      ))}
                      {isAdmin && (
                        <th className="text-left font-medium text-muted-foreground px-4 py-3">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-border last:border-b-0 hover:bg-muted/30"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-4 py-3">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                        {isAdmin && (
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => handleEdit(row.original)}
                                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                              >
                                <Pencil className="size-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(row.original)}
                                className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                              >
                                <Trash2 className="size-4" />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-end gap-2 mt-4">
              <span className="text-sm text-muted-foreground">
                Showing{' '}
                {table.getState().pagination.pageIndex *
                  table.getState().pagination.pageSize +
                  1}
                -
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) *
                    table.getState().pagination.pageSize,
                  table.getPrePaginationRowModel().rows.length
                )}{' '}
                of {table.getPrePaginationRowModel().rows.length} results
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <ChevronLeft className="size-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Next
                <ChevronRight className="size-4" />
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation */}
      <ConfirmModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        title="Are you sure you want to delete this product?"
        description="This action cannot be undone."
        confirmLabel="Delete Product"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Create/Edit Dialog */}
      {isAdmin && (
        <ProductFormDialog
          open={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onProductCreated={refreshProducts}
          selectedProduct={selectedProduct}
          productFamilies={productFamilies.map((f) => ({ label: f, value: f }))}
        />
      )}
    </div>
  );
}
